/**
 * Modern Selector Factory Utilities
 *
 * Clean utilities for our standardized selector architecture.
 * NO UI dependencies - pure domain logic only.
 *
 * These utilities support the EntitySelectorFactory pattern.
 */

import { createSelector } from 'reselect';
import {
  Map,
  List
} from 'immutable';
import { RootState } from '../../types/redux';
import {
  DirectSelector,
  ParameterizedSelector,
} from '../../types/selectors';
import { EntityId } from '../../types/utilities';

/**
 * Creates an Immutable List selector from entity Map
 * Used in our modern EntitySelectorFactory pattern
 *
 * Note: This returns List<T>, use .toArray() for UI components
 */
export const createEntityListSelector = <T>(
  entitySelector: (state: RootState) => Map<string, T>,
  _stateKey?: string // For consistency with existing usage, but not used
) => {
  return createSelector(
    [entitySelector],
    (entities) => {
      if (!entities || typeof entities.toList !== 'function') {
        return List<T>();
      }
      return entities.toList();
    }
  );
};

/**
 * Creates a memoized "by id" lookup selector for an entity slice.
 *
 * Why this exists
 * ===============
 * Every entity in this codebase used to define its own `select<X>ById`
 * roughly like this:
 *
 *     export const selectFoodById: ParameterizedSelector<EntityId, Food | null> =
 *       (id: EntityId) =>
 *         createSelector([selectFoodsMap], (foods) => foods.get(id) || null);
 *
 * The problem with that shape is that calling it during render
 * (`useSelector(selectFoodById(id))`) creates a *new* reselect instance
 * on every call, with a cache size of 1. That cache never sees a hit
 * across renders, so the selector ALWAYS recomputes — and worse, since
 * `entities.get(id) || null` allocates a fresh `null` reference when
 * the entity is missing, the value reference is unstable too.
 *
 * The unstable reference forces every consuming component to re-render
 * on any unrelated Redux dispatch, even though the entity itself is
 * unchanged. With dozens of `*ById` selectors and route bodies that
 * subscribe to several at once (e.g. spec edit pages), this is a
 * material driver of low INP / "the page feels janky" complaints.
 *
 * What this factory does
 * ======================
 * 1. Builds a single shared selector keyed on the entity Map.
 * 2. Returns a curried lookup function: `(id) => entity | null`.
 * 3. The returned `ParameterizedSelector<EntityId, T | null>` retrieves
 *    that lookup function from the cached selector and invokes it with
 *    `id`. Lookup is O(log n) in the Immutable Map; the lookup function
 *    is reused as long as the underlying Map reference is unchanged.
 *
 * Note: this does not memoise across `id` values — but Immutable's
 * `.get(id)` already returns the same reference for the same key when
 * the Map is unchanged, so consumers see stable references for
 * unchanged entities, which is the only guarantee React + react-redux
 * actually need.
 *
 * Usage
 * =====
 *     import { createEntityByIdSelector } from '../../store/selectors/filterFactory';
 *
 *     export const selectFoodById = createEntityByIdSelector(selectFoodsMap);
 */
export function createEntityByIdSelector<
  T,
  Id extends string = string,
>(
  entitiesMapSelector: (state: RootState) => Map<string, T>,
): ParameterizedSelector<Id, T | null> {
  // Single shared reselect that closes over the entities map and
  // returns a stable lookup function whenever the map is unchanged.
  const lookupSelector = createSelector(
    [entitiesMapSelector],
    (entities) => (id: Id) => entities.get(id as unknown as string) || null,
  );

  return (id: Id): DirectSelector<T | null> =>
    (state: RootState) => lookupSelector(state)(id);
}

/**
 * Creates a memoized "filtered list by parent id" selector for entities
 * with a parent-foreign-key field.
 *
 * Several entities in this codebase model parent/child relationships
 * with a foreign key on the child (e.g. `ProfileDimension.profile`,
 * `ProfileParameter.profile`). The pre-existing pattern was:
 *
 *     export const selectChildrenByParentId: ParameterizedSelector<EntityId, List<Child>> =
 *       (parentId) => createSelector([selectChildrenList], (children) =>
 *         children.filter((c) => c.parent === parentId)
 *       );
 *
 * Same un-memoized issue as `createEntityByIdSelector`. This factory
 * shares the underlying filter selector and curries the parent id, so
 * the filtered List has a stable reference whenever the underlying
 * children list is unchanged.
 *
 * Usage
 * =====
 *     export const selectProfileDimensionsListById = createEntityListByParentIdSelector(
 *       selectProfileDimensionsList,
 *       'profile',
 *     );
 */
export function createEntityListByParentIdSelector<T extends Record<string, unknown>>(
  entitiesListSelector: (state: RootState) => List<T>,
  parentField: keyof T,
): ParameterizedSelector<EntityId, List<T>> {
  // Reselect can't natively memoize on a parameter so we use the
  // closure trick: build a per-parentId selector lazily and cache it.
  // This means the *first* call for a new parentId allocates the
  // selector; subsequent calls hit the cache.
  //
  // NB: explicitly use the native JS Map (globalThis.Map) for the
  // cache. The top of this file imports `Map` from immutable for the
  // `Map<string, T>` type annotations on the public API, so a bare
  // `new Map()` here would build an Immutable Map and miss every
  // lookup.
  const cache = new globalThis.Map<string, DirectSelector<List<T>>>();

  return (parentId: EntityId): DirectSelector<List<T>> => {
    const cacheKey = String(parentId);
    const existing = cache.get(cacheKey);
    if (existing) return existing;

    const selector = createSelector(
      [entitiesListSelector],
      (entities) => entities.filter((entity: T) => entity[parentField] === parentId),
    );

    cache.set(cacheKey, selector);
    return selector;
  };
}
